import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { DotLottie, EventType } from "@lottiefiles/dotlottie-web";
import { Box, BoxProps } from "@/ui";
import { useColorMode } from "@/ui/hooks";

type Props = {
  src?: string;
  props?: BoxProps;
  themeId?: string;
  loop?: boolean;
  autoplay?: boolean;
  events?: [EventType, Cb][];
  textSlots?: Record<string, string>;
  getDotLottie?: (dotLottie: DotLottie | null) => void;
};
export type { DotLottie, EventType };

type TextSlotProperty = { k?: [{ s?: { t?: string } }] };

export default function Lottie({
  src,
  props,
  themeId,
  loop = true,
  autoplay = true,
  events,
  textSlots,
  getDotLottie
}: Props) {
  const [size, setSize] = useState<{ width: number; height: number }>();
  const { colorMode } = useColorMode();
  const { sx, ...boxProps } = props ?? {};

  const dotLottieRef = useRef<DotLottie | null>(null);
  const desiredThemeRef = useRef("");
  desiredThemeRef.current = themeId ?? (colorMode === "dark" ? "Dark" : "Default");
  const textSlotsRef = useRef(textSlots);
  textSlotsRef.current = textSlots;

  const applyAppearance = (dotLottie: DotLottie) => {
    if (!dotLottie.setTheme(desiredThemeRef.current)) dotLottie.resetTheme();

    const slots: Record<string, unknown> = {};
    Object.entries(textSlotsRef.current ?? {}).forEach(([id, text]) => {
      const property = dotLottie.getSlot(id) as TextSlotProperty | undefined;
      const textDocument = property?.k?.[0]?.s;
      if (!textDocument) return;
      textDocument.t = text;
      slots[id] = { p: property };
    });
    if (Object.keys(slots).length) dotLottie.setSlots(slots);
  };

  useEffect(() => {
    if (dotLottieRef.current?.isLoaded) applyAppearance(dotLottieRef.current);
  }, [colorMode, themeId]);

  return (
    <Box
      w={size && `${size.width}px`}
      maxW="100%"
      {...boxProps}
      sx={{ ...(size && { aspectRatio: `${size.width} / ${size.height}` }), ...sx }}
    >
      <DotLottieReact
        src={src}
        loop={loop}
        autoplay={autoplay}
        renderConfig={{ autoResize: true }}
        // Animation is scuffed on first load, so we need to do all of this to fix it
        dotLottieRefCallback={(dotLottie) => {
          dotLottieRef.current = dotLottie;
          getDotLottie?.(dotLottie);
          if (!dotLottie) return;
          dotLottie.addEventListener("load", () => {
            applyAppearance(dotLottie);
            flushSync(() => setSize(dotLottie.animationSize()));
            dotLottie.resize();
          });
          events?.forEach(([event, callback]) => dotLottie.addEventListener(event, callback));
        }}
      />
    </Box>
  );
}
